// Angular
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
// RxJS
import { tap, catchError, map, mergeMap, retry, delay } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
// NGRX
import { Actions, Effect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
// Auth actions
import { AuthActionTypes, Login, Logout, Register, UserLoaded } from '../_actions/auth.actions';
import { AppState } from '../../reducers';
import { environment } from '../../../../environments/environment';

import { AuthenticateService } from '@app/api/authenticate.service';

// Claim key cho role trong JWT token (.NET Identity)
const JWT_ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

@Injectable()
export class AuthEffects {

    /**
     * Decode JWT token và lấy roles từ claims.
     * API getMyInfo KHÔNG trả roles — roles nằm trong JWT payload.
     */
    private getRolesFromToken(token: string): string[] {
        try {
            const parts = token.split('.');
            if (parts.length !== 3) return [];
            let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            while (payload.length % 4) payload += '=';
            const decoded = JSON.parse(atob(payload));
            const roleClaim = decoded[JWT_ROLE_CLAIM];
            if (!roleClaim) return [];
            return Array.isArray(roleClaim) ? roleClaim : [roleClaim];
        } catch (e) {
            console.error('[AUTH] Error decoding JWT:', e);
            return [];
        }
    }

    /**
     * Kiểm tra JWT token đã hết hạn chưa.
     * Trả về true nếu token hết hạn hoặc không parse được exp.
     */
    private isTokenExpired(token: string): boolean {
        try {
            const parts = token.split('.');
            if (parts.length !== 3) return true;
            let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            while (payload.length % 4) payload += '=';
            const decoded = JSON.parse(atob(payload));
            if (!decoded.exp) return false;
            const expiryDate = new Date(decoded.exp * 1000);
            return expiryDate.getTime() < Date.now();
        } catch (e) {
            console.error('[AUTH] Error checking token expiry:', e);
            return true;
        }
    }

    /**
     * Xóa token + permissions khỏi localStorage.
     */
    private clearAuthStorage(): void {
        localStorage.removeItem(environment.authTokenKey);
        localStorage.removeItem('permissions');
    }

    /**
     * Gọi API getMyInfo và trả về UserLoaded action.
     */
    private loadUserFromAPI(token: string): Observable<Action> {
        const rolesFromToken = this.getRolesFromToken(token);

        console.log('[AUTH Effect] loadUserFromAPI called, token length:', token ? token.length : 0);

        return this.authenticate.apiAuthenticateGetMyInfoGet().pipe(
            // Retry 1 lần cho lỗi tạm thời (500, network timeout)
            retry(1),
            map(_user => {
                console.log('[AUTH Effect] getMyInfo raw response:', JSON.stringify(_user).substring(0, 300));
                if (_user && _user.success) {
                    const userData = _user.user || _user.data;
                    if (!userData) {
                        console.error('[AUTH Effect] success=true but no user/data field. Keys:', Object.keys(_user));
                        return { type: 'NO_ACTION' } as Action;
                    }
                    const roles = rolesFromToken.length > 0
                        ? rolesFromToken
                        : (_user.roles || (userData && userData.roles) || []);
                    const userWithRoles = {
                        ...userData,
                        roles: roles
                    };
                    console.log('[AUTH Effect] ✅ User loaded:', userWithRoles.full_name || userWithRoles.email || 'unknown');
                    return new UserLoaded({ user: userWithRoles });
                } else {
                    console.warn('[AUTH Effect] getMyInfo returned success=false or null — keeping login state');
                    return { type: 'NO_ACTION' } as Action;
                }
            }),
            catchError(error => {
                console.error('[AUTH] getMyInfo error:', error.status, error.message);
                if (error.status === 401 || error.status === 403) {
                    localStorage.removeItem(environment.authTokenKey);
                    localStorage.removeItem('permissions');
                    return of(new Logout());
                }
                // Lỗi khác (500, network): KHÔNG logout, giữ token + loggedIn state.
                // User vẫn thấy header đăng nhập (không bị hiện "Đăng nhập / Đăng ký").
                // Có thể retry bằng cách F5 lại.
                return of({ type: 'NO_ACTION' } as Action);
            })
        );
    }

    /**
     * init$ — Lắng nghe ROOT_EFFECTS_INIT.
     * Chỉ dispatch Login action, loadUser$ sẽ tự động gọi getMyInfo.
     * Tách riêng để mọi login flow (init, modal, login page) đều trigger loadUser$.
     */
    @Effect()
    init$ = this.actions$.pipe(
        ofType(ROOT_EFFECTS_INIT),
        map(() => {
            const userToken = localStorage.getItem(environment.authTokenKey);
            if (userToken && userToken.length > 10) {
                if (this.isTokenExpired(userToken)) {
                    console.warn('[AUTH] Token expired, clearing auth storage');
                    this.clearAuthStorage();
                    return { type: 'NO_ACTION' } as Action;
                }
                return new Login({ authToken: userToken });
            } else if (userToken) {
                this.clearAuthStorage();
            }
            return { type: 'NO_ACTION' } as Action;
        })
    );

    /**
     * loadUser$ — Lắng nghe Login action từ mọi nguồn (init$, modal login, login page).
     * Gọi API getMyInfo để lấy thông tin user → dispatch UserLoaded.
     * Đảm bảo user data luôn được load bất kể login từ đâu.
     */
    @Effect()
    loadUser$ = this.actions$.pipe(
        ofType<Login>(AuthActionTypes.Login),
        mergeMap(action => this.loadUserFromAPI(action.payload.authToken))
    );

    // login$ save token vào localStorage
    @Effect({dispatch: false})
    login$ = this.actions$.pipe(
        ofType<Login>(AuthActionTypes.Login),
        tap(action => {
            localStorage.setItem(environment.authTokenKey, action.payload.authToken);
        }),
    );

    @Effect({dispatch: false})
    logout$ = this.actions$.pipe(
        ofType<Logout>(AuthActionTypes.Logout),
        tap(() => {
            this.clearAuthStorage();
            this.router.navigate(['/homepage']);
        })
    );

    @Effect({dispatch: false})
    register$ = this.actions$.pipe(
        ofType<Register>(AuthActionTypes.Register),
        tap(action => {
            localStorage.setItem(environment.authTokenKey, action.payload.authToken);
        })
    );

    private returnUrl: string;

    constructor(private actions$: Actions,
                private router: Router,
                private authenticate: AuthenticateService,
                private store: Store<AppState>) {

		this.router.events.subscribe(event => {
			if (event instanceof NavigationEnd) {
				this.returnUrl = event.url;
			}
		});
	}
}
